/**
 * School API Service
 *
 * Provides API methods for managing schools and retrieving school-related data.
 * Uses the Repository pattern to abstract API communication.
 *
 * @example
 * ```typescript
 * constructor(private schoolApi: SchoolApiService) {}
 *
 * ngOnInit() {
 *   this.schoolApi.getSchools(1, 20, 'Lincoln').subscribe(response => {
 *     this.schools = response.items;
 *   });
 * }
 * ```
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { School, SchoolStatistics } from '../../../core/models';

@Injectable({
  providedIn: 'root'
})
export class SchoolApiService {
  /**
   * HTTP client for making API requests
   */
  private readonly http = inject(HttpClient);

  /**
   * Base API URL for school endpoints
   */
  private readonly apiUrl = `${environment.apiBaseUrl}/schools`;

  /**
   * Retrieves a paginated list of schools with optional search
   *
   * @param page - Current page number (1-indexed)
   * @param perPage - Number of items per page
   * @param search - Optional search query to filter schools by name
   * @returns Observable of paginated school data
   *
   * @example
   * ```typescript
   * this.schoolApi.getSchools(1, 20, 'Lincoln').subscribe(data => {
   *   console.log(`Found ${data.total} schools`);
   *   this.schools = data.items;
   * });
   * ```
   */
  getSchools(
    page: number,
    perPage: number,
    search?: string
  ): Observable<{ items: School[]; total: number; page: number; per_page: number; last_page: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    // API returns { success, data: School[], meta: { total, current_page, per_page, last_page } }
    return this.http
      .get<{ success: boolean; data: School[]; meta: { total: number; current_page: number; per_page: string; last_page: number } }>(this.apiUrl, { params })
      .pipe(map(response => ({
        items: response.data,
        total: response.meta.total,
        page: response.meta.current_page,
        per_page: parseInt(response.meta.per_page, 10),
        last_page: response.meta.last_page
      })));
  }

  /**
   * Retrieves detailed information for a specific school
   *
   * @param id - Unique identifier of the school
   * @returns Observable of school details
   *
   * @example
   * ```typescript
   * this.schoolApi.getSchool(123).subscribe(school => {
   *   console.log(`School: ${school.name}`);
   *   this.school = school;
   * });
   * ```
   */
  getSchool(id: number): Observable<School> {
    return this.http
      .get<{ success: boolean; message: string; data: School }>(`${this.apiUrl}/${id}`)
      .pipe(map(response => response.data));
  }

  /**
   * Retrieves statistical data for a specific school within a date range
   *
   * @param id - Unique identifier of the school
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable of school statistics
   *
   * @example
   * ```typescript
   * this.schoolApi.getSchoolStatistics(123, '2026-01-01', '2026-01-31').subscribe(stats => {
   *   console.log(`Attendance: ${stats.attendanceRate}%`);
   *   this.statistics = stats;
   * });
   * ```
   */
  getSchoolStatistics(
    id: number,
    startDate: string,
    endDate: string
  ): Observable<SchoolStatistics> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http
      .get<{ success: boolean; message: string; data: SchoolStatistics }>(
        `${this.apiUrl}/${id}/statistics`,
        { params }
      )
      .pipe(map(response => response.data));
  }
}
